/**
 * OVERSIKTEN «DELINGER» (F9, runde 4. oktober): hvem utenfor organisasjonen som har tilgang til
 * hva, på tvers av alt som er delt — med filter, forlengelse og «Trekk tilbake» på mange på en gang.
 *
 * Cella `share:list` åpner den (standard bare superadmin; Atle: «bør kunne velge hvilke nivå som
 * skal ha tilgang til slikt»). Den som ser oversikten, kan også forlenge og trekke tilbake herfra:
 * det er det oversikten er til, og en egen celle for det ville vært en bryter uten mening alene.
 *
 * LISTA HAR TO KILDER (F17): delingene i organisasjonens eget hus, og delingene organisasjonen har
 * laget av en ANNEN organisasjons delte advance (byrået som deler festivalens advance videre). De
 * siste har `ownerOrganizationName` satt — de ligger i det andre huset, men er våre å styre.
 */
import { z } from 'zod';

import { DEFAULT_PAGE_SIZE, MAX_CURSOR_LENGTH, MAX_PAGE_SIZE, page } from '../http/pagination.js';
import { shareKindSchema, shareObjectTypeSchema, sharePermissionSchema } from './shares.js';
import { wireInstant } from './wire-time.js';

export const SHARE_OVERVIEW_STATUSES = ['live', 'expired', 'revoked', 'all'] as const;
export const shareOverviewStatusSchema = z.enum(SHARE_OVERVIEW_STATUSES);
export type ShareOverviewStatus = z.infer<typeof shareOverviewStatusSchema>;

/** Nyeste først. Id-en er UUIDv7, så den ER rekkefølgen delingene ble laget i. */
export const SHARE_OVERVIEW_SORT_SPEC = 'created:desc';

export const listSharesOverviewQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    cursor: z.string().max(MAX_CURSOR_LENGTH).optional(),
    type: shareObjectTypeSchema.optional(),
    kind: shareKindSchema.optional(),
    /** Standard er de levende — det er dem man rydder i. */
    status: shareOverviewStatusSchema.default('live'),
    /** Mottakerens navn eller e-post, eller navnet på det som er delt. */
    q: z.string().trim().min(1).max(120).optional(),
  })
  .meta({ id: 'ListSharesOverviewQuery' });
export type ListSharesOverviewQuery = z.infer<typeof listSharesOverviewQuerySchema>;

export const shareOverviewItemSchema = z
  .object({
    id: z.uuid(),
    objectType: shareObjectTypeSchema,
    objectId: z.uuid(),
    /** Navnet på det som er delt (programposten, vakta, salen …). Tomt når objektet er borte. */
    objectTitle: z.string(),
    objectStartsAt: z.string().nullable(),
    festivalId: z.uuid().nullable(),
    editionId: z.uuid().nullable(),
    kind: shareKindSchema,
    personId: z.uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
    permission: sharePermissionSchema,
    scopes: z.array(z.string()).nullable(),
    expiresAt: wireInstant.nullable(),
    expired: z.boolean(),
    createdAt: wireInstant,
    lastOpenedAt: wireInstant.nullable(),
    confirmedAt: wireInstant.nullable(),
    revokedAt: wireInstant.nullable(),
    grantedByName: z.string().nullable(),
    /** F17: delt av den andre parten i en delt advance. Null når organisasjonen selv delte. */
    grantedByOrganizationName: z.string().nullable(),
    /** F17: delingen ligger i en annen organisasjons hus (deres programpost). Null når den er vår. */
    ownerOrganizationName: z.string().nullable(),
  })
  .meta({ id: 'ShareOverviewItem' });
export type ShareOverviewItem = z.infer<typeof shareOverviewItemSchema>;

export const shareOverviewListResponseSchema = page(
  shareOverviewItemSchema,
  'ShareOverviewListResponse',
);
export type ShareOverviewListResponse = z.infer<typeof shareOverviewListResponseSchema>;

/** Så mange på én gang. Lista er sidevis med 50; to sider er nok til å rydde etter en sesong. */
export const MAX_BULK_SHARES = 100;

const grantIds = z.array(z.uuid()).min(1).max(MAX_BULK_SHARES);

export const extendSharesRequestSchema = z
  .object({ grantIds, expiresAt: z.iso.datetime() })
  .meta({ id: 'ExtendSharesRequest' });
export type ExtendSharesRequest = z.infer<typeof extendSharesRequestSchema>;

export const revokeSharesRequestSchema = z.object({ grantIds }).meta({ id: 'RevokeSharesRequest' });
export type RevokeSharesRequest = z.infer<typeof revokeSharesRequestSchema>;

/** Hvilke som ble gjort, og hvilke som ikke kunne (allerede trukket, borte, eller ikke våre). */
export const bulkSharesResponseSchema = z
  .object({ done: z.array(z.uuid()), skipped: z.array(z.uuid()) })
  .meta({ id: 'BulkSharesResponse' });
export type BulkSharesResponse = z.infer<typeof bulkSharesResponseSchema>;
